import React from 'react';
import { useHistory } from 'react-router';
import { formatDistance, unpackToDateTime } from '@campfire/hot-date';
import { TabletButton } from '@campfire/tablet-button';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  Typography,
} from '@material-ui/core';
import { CheckCircle, Settings as SettingsIcon } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { useCampfireFetch } from '../../../../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../../../../global/config/useApiUrl';
import { useCampfireTheme } from '../../../../../../../../theme/useCampfireTheme';
import { AlertCard } from '../../../../../../../../common/cards/alert-card/AlertCard';
import { TemplateSnackbar, SnackBarOptions } from '../../../../../../../../common/snackbars/TemplateSnackbar';
import { useCommonStyles } from '../../commonStyles';

const useStyles = makeStyles(() =>
  createStyles({
    sectionTitle: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    sectionDescription: {
      fontSize: '14px',
      color: '#808080',
      marginTop: '0.25rem',
      marginBottom: '1.5rem',
    },
    switchLabel: {
      fontWeight: 500,
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
    },
    switchDescription: {
      fontSize: '13px',
      color: '#808080',
    },
    statusTitle: {
      fontWeight: 700,
      fontSize: '0.875rem',
      lineHeight: '1.125rem',
      letterSpacing: '0.008em',
    },
    statusSubtitle: {
      fontWeight: 400,
      fontSize: '0.813rem',
      lineHeight: '1.125rem',
      letterSpacing: '0.008em',
      opacity: '60%',
    },
    statusCaption: {
      fontWeight: 400,
      fontStyle: 'italic',
      fontSize: '0.75rem',
      lineHeight: '1.375rem',
      letterSpacing: '0.008em',
      opacity: '60%',
    },
    authorisationText: {
      fontSize: '14px',
      fontWeight: 400,
      opacity: '60%',
      lineHeight: '18px',
      letterSpacing: '0.2px',
    },
  })
);

type ISeekVolunteerManageData = {
  organisationName: string | null;
  organisationId: string | null;
  userEmail: string | null;
  region: string | null;
  autoPublishFunnels: boolean;
  dateCreated?: string;
};

export function SeekVolunteerSetups() {
  const classes = useStyles();
  const commonStyles = useCommonStyles();
  const { theme, isSm } = useCampfireTheme();
  const apiUrl = useApiUrl();
  const history = useHistory();

  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isDisconnecting, setIsDisconnecting] = React.useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);
  const [manageData, setManageData] = React.useState<ISeekVolunteerManageData | null>(null);
  const [autoPublishFunnels, setAutoPublishFunnels] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<SnackBarOptions>();

  const manageFetch = useCampfireFetch<ISeekVolunteerManageData>({ defer: true });
  const saveFetch = useCampfireFetch({ defer: true });
  const disconnectFetch = useCampfireFetch({ defer: true });

  React.useEffect(() => {
    manageFetch
      .run({
        url: `${apiUrl}/wm/integrations/seek-volunteer/manage/get`,
        method: 'get',
      })
      .then((response) => {
        const {
          data: { data },
        } = response;
        setManageData(data);
        setAutoPublishFunnels(data.autoPublishFunnels);
      })
      .catch(() => {
        setLoadError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const onSave = async () => {
    setIsSaving(true);
    try {
      await saveFetch.run({
        url: `${apiUrl}/wm/integrations/seek-volunteer/manage/save`,
        method: 'post',
        data: { autoPublishFunnels },
      });
      setSnackbar({ variant: 'success', open: true, message: 'Settings saved' });
    } catch {
      setSnackbar({ variant: 'error', open: true, message: 'Failed to save settings' });
    } finally {
      setIsSaving(false);
    }
  };

  const onDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await disconnectFetch.run({
        url: `${apiUrl}/wm/integrations/seek-volunteer/manage/disconnect`,
        method: 'post',
        data: {},
      });
      history.push('/admin/admin-console/apps');
    } catch {
      setSnackbar({ variant: 'error', open: true, message: 'Failed to disconnect' });
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectDialog(false);
    }
  };

  const getRegionLabel = (region: string | null) => {
    if (region === 'nz') return 'New Zealand';
    return 'Australia';
  };

  const getLastUpdatedText = () => {
    if (manageData?.dateCreated) {
      try {
        return `Connected ${formatDistance(unpackToDateTime(manageData.dateCreated)).toLowerCase()}`;
      } catch {
        return 'Connected recently';
      }
    }
    return 'Connected recently';
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  if (loadError) {
    return (
      <Box p={2}>
        <AlertCard variant='urgent' title='Unable to load settings'>
          Failed to load SEEK Volunteer settings. Please try again later.
        </AlertCard>
      </Box>
    );
  }

  return (
    <Box>
      <Box className={commonStyles.containerPanel}>
        <Typography variant='h2' className={commonStyles.headingTitle}>
          Your Account
        </Typography>
        <Box display='flex' flexDirection={isSm ? 'column' : 'row'} py='1rem'>
          <Box flex={1} borderRight={isSm ? 'none' : '1px solid #dfdfdf'} mr='1rem'>
            <Typography className={commonStyles.sectionHeading}>Status</Typography>
            <Box display='flex' alignItems='flex-start' pt='1rem' pr='1rem'>
              <CheckCircle fontSize='small' style={{ color: theme.alert.green.light }} />
              <Box pl='0.5rem'>
                <Typography className={classes.statusTitle}>Active & Connected</Typography>
                <Typography className={classes.statusSubtitle}>
                  {manageData?.organisationName || 'Connected to SEEK Volunteer'}
                </Typography>
                {manageData?.userEmail && (
                  <Typography className={classes.statusSubtitle}>{manageData.userEmail}</Typography>
                )}
                <Typography className={classes.statusCaption}>
                  Region: {getRegionLabel(manageData?.region ?? null)}
                </Typography>
                <Typography className={classes.statusCaption}>{getLastUpdatedText()}</Typography>
              </Box>
            </Box>
          </Box>
          <Box flex={1}>
            <Typography className={commonStyles.sectionHeading}>Authorisations</Typography>
            <Box pt='0.5rem' className={classes.authorisationText}>
              <p>Volaby is authorised to:</p>
              <ul style={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
                <li>Publish volunteer opportunities to SEEK Volunteer</li>
                <li>Receive applications from SEEK Volunteer</li>
                <li>Access your organisation information</li>
              </ul>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box className={commonStyles.containerPanel}>
        <Typography className={classes.sectionTitle}>
          <SettingsIcon style={{ fontSize: '14px', paddingRight: '5px' }} />
          Funnel Publishing
        </Typography>
        <Typography variant='body2' className={classes.sectionDescription}>
          Configure how funnels are published to SEEK Volunteer
        </Typography>

        <Box mt={2}>
          <Box display='flex' alignItems='center'>
            <Switch
              checked={autoPublishFunnels}
              onChange={() => setAutoPublishFunnels(!autoPublishFunnels)}
              color='primary'
            />
            <Box ml={1}>
              <Typography className={classes.switchLabel}>Auto-publish new funnels</Typography>
              <Typography variant='body2' className={classes.switchDescription}>
                When enabled, newly created funnels will be automatically published as opportunities on SEEK Volunteer.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box display='flex' justifyContent='flex-end' mt='1rem' mr='1.875rem' style={{ gap: '0.5rem' }}>
        <TabletButton
          onClick={() => setShowDisconnectDialog(true)}
          variant='outlined'
          style={{
            borderColor: theme.alert.red.light,
            color: theme.alert.red.light,
            boxSizing: 'border-box',
            borderRadius: '4px',
            padding: '7px 12px',
            fontWeight: 600,
            fontSize: '13px',
          }}
        >
          Disconnect
        </TabletButton>
        <TabletButton
          onClick={onSave}
          color='primary'
          variant='contained'
          disabled={isSaving}
          style={{
            backgroundColor: theme.color.secondary.main900,
            color: '#ffffff',
            boxSizing: 'border-box',
            borderRadius: '4px',
            padding: '7px 12px',
            fontWeight: 600,
            fontSize: '13px',
          }}
        >
          {isSaving ? <CircularProgress size={16} style={{ color: '#fff' }} /> : 'Save'}
        </TabletButton>
      </Box>

      <Dialog open={showDisconnectDialog} onClose={() => setShowDisconnectDialog(false)}>
        <DialogTitle>Disconnect SEEK Volunteer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will revoke Volaby&apos;s access to your SEEK Volunteer account. Opportunities will no longer be
            published and incoming applications will stop being received. You can reconnect at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <TabletButton onClick={() => setShowDisconnectDialog(false)} disabled={isDisconnecting}>
            Cancel
          </TabletButton>
          <TabletButton onClick={onDisconnect} disabled={isDisconnecting} style={{ color: theme.alert.red.light }}>
            {isDisconnecting ? <CircularProgress size={16} /> : 'Disconnect'}
          </TabletButton>
        </DialogActions>
      </Dialog>

      {snackbar && <TemplateSnackbar {...snackbar} onClose={() => setSnackbar(undefined)} />}
    </Box>
  );
}
